/**
 * Browser error monitoring (launch, 2026-10-18). Off until the server hands
 * out a DSN (SENTRY_FRONTEND_DSN, served by GET /api/auth/client-config),
 * and the SDK itself is only downloaded when it is on, so it costs the
 * bundle nothing otherwise. No personal data: no user, no cookies, and
 * tokens stripped from every URL (share links, verify and reset links).
 */
type SentryModule = typeof import('@sentry/react');

let sentry: SentryModule | null = null;

/** Path segments that are secrets: /p/<token>, /aroll/verify/<token>, ... */
const TOKEN_PATHS = /(\/(?:p|portal\/share|verify|reset-password|share|email-change|aroll\/p)\/)[^/?#]+/g;

/** Drop the query string and replace token path segments. Exported for tests. */
export function scrubUrl(url: string): string {
  const noQuery = url.split(/[?#]/)[0] ?? url;
  return noQuery.replace(TOKEN_PATHS, '$1:token');
}

export async function initFrontendSentry(): Promise<void> {
  try {
    const res = await fetch('/api/auth/client-config', { credentials: 'omit' });
    if (!res.ok) return;
    const cfg = (await res.json()) as { sentryDsn?: string | null; sentryEnvironment?: string };
    if (!cfg.sentryDsn) return;
    const mod = await import('@sentry/react');
    mod.init({
      dsn: cfg.sentryDsn,
      environment: cfg.sentryEnvironment,
      sendDefaultPii: false,
      tracesSampleRate: 0,
      beforeSend(event) {
        if (event.request) {
          delete event.request.cookies;
          delete event.request.query_string;
          if (event.request.url) event.request.url = scrubUrl(event.request.url);
          if (event.request.headers) delete event.request.headers.Referer;
        }
        delete event.user;
        return event;
      },
      beforeBreadcrumb(crumb) {
        const data = crumb.data as Record<string, unknown> | undefined;
        if (data) {
          for (const k of ['url', 'from', 'to']) {
            if (typeof data[k] === 'string') data[k] = scrubUrl(data[k] as string);
          }
        }
        return crumb;
      },
    });
    sentry = mod;
  } catch {
    /* monitoring must never break the app */
  }
}

/** Report an error caught by the app (error boundary). No-op when off. */
export function reportError(err: unknown, extra?: Record<string, unknown>): void {
  try {
    sentry?.captureException(err, extra ? { extra } : undefined);
  } catch {
    /* ignore */
  }
}
